import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import useResponsive from '../hooks/useResponsive';
import { c, t, radius, shadow } from '../theme';

// A floating return path to the cart.
//
// /cart is linked from exactly one screen, so someone browsing products has no
// visible way back to checkout without going Home first. This follows them
// while they are shopping and disappears the rest of the time.
//
// Count only, never a total: prices are not final until an admin confirms
// them, and a number on a floating button that later changes is the worst kind
// of number to show a buyer.

// Only where someone is accumulating items. On Wallet or Profile they have
// left the shopping flow and a cart chasing them there is nagging.
const BROWSE_ROUTES = ['/dashboard', '/products', '/stores'];

// Fired by the add-to-cart handlers so the badge reacts immediately rather
// than waiting for the next poll.
export const CART_CHANGED = 'pv:cart-changed';
export const notifyCartChanged = () => {
  try {
    window.dispatchEvent(new CustomEvent(CART_CHANGED));
  } catch (err) {
    // A missing badge update must never break adding to cart.
  }
};

const CartFab = () => {
  const { user } = useAuth();
  const location = useLocation();
  const { isMobile } = useResponsive();

  const [count, setCount] = useState(0);
  const [bump, setBump] = useState(false);
  const prevCount = useRef(0);

  const onBrowseRoute = BROWSE_ROUTES.some((r) => location.pathname.startsWith(r));
  // Never over the cart itself, and never over checkout.
  const onCart = location.pathname.includes('/cart');

  const fetchCount = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data } = await supabase
        .from('cart_items')
        .select('id')
        .eq('user_id', user.id);
      // Distinct lines, not units. "2" reads as two products in the cart;
      // summing quantity would say "7" for 5 paracetamol and 2 panadol, which
      // nobody reads as the number of things they picked.
      if (data) setCount(data.length);
    } catch (err) {
      // decorative; stay silent
    }
  }, [user?.id]);

  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, 20000);
    window.addEventListener(CART_CHANGED, fetchCount);
    return () => {
      clearInterval(id);
      window.removeEventListener(CART_CHANGED, fetchCount);
    };
  }, [fetchCount]);

  // Refetch on navigation: quantities change on the cart screen too.
  useEffect(() => { fetchCount(); }, [location.pathname, fetchCount]);

  // A brief bump when the count rises. Adding an item currently fires a toast
  // and nothing else; this is what makes "it went in the cart" actually land.
  useEffect(() => {
    if (count > prevCount.current && prevCount.current !== 0) {
      setBump(true);
      const id = setTimeout(() => setBump(false), 420);
      prevCount.current = count;
      return () => clearTimeout(id);
    }
    prevCount.current = count;
  }, [count]);

  if (!user || count === 0 || onCart || !onBrowseRoute) return null;

  return (
    <>
      <style>{`
        @keyframes pvCartBump {
          0%   { transform: scale(1); }
          35%  { transform: scale(1.14); }
          70%  { transform: scale(0.97); }
          100% { transform: scale(1); }
        }
        @keyframes pvCartIn {
          0%   { opacity: 0; transform: translateY(14px) scale(0.9); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        /* Capsules tip into the cart every so often. The motion lives in the
           first 7% of a 13s cycle and the rest is stillness, so the loop runs
           on the compositor with no timers and no re-renders — and a long gap
           between bursts is what keeps it charming rather than nagging. */
        @keyframes pvCapsuleDrop {
          0%      { opacity: 0; transform: translateY(-11px) rotate(-25deg); }
          2%      { opacity: 1; }
          6%      { opacity: 1; transform: translateY(7px) rotate(12deg); }
          7.5%    { opacity: 0; transform: translateY(9px) rotate(16deg); }
          100%    { opacity: 0; transform: translateY(9px) rotate(16deg); }
        }
      `}</style>

      <Link
        to="/cart"
        aria-label={count + ' items in cart'}
        style={{
          position: 'fixed',
          right: isMobile ? '16px' : '28px',
          // Clears the bottom tab bar on mobile and the gesture inset beneath
          // it. On desktop the nav is a sidebar, so it can sit lower.
          bottom: isMobile
            ? 'calc(78px + env(safe-area-inset-bottom, 0px))'
            : '28px',
          zIndex: 180,
          display: 'flex',
          alignItems: 'center',
          gap: '9px',
          padding: '12px 16px',
          borderRadius: radius.pill,
          background: c.brand,
          border: '1px solid ' + c.brandDeep,
          boxShadow: shadow.raised,
          textDecoration: 'none',
          animation: bump
            ? 'pvCartBump 0.42s cubic-bezier(0.34, 1.56, 0.64, 1)'
            : 'pvCartIn 0.26s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      >
        <span style={{ position: 'relative', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <ShoppingCart size={19} strokeWidth={2.2} color={c.white} />
          {/* Purely decorative, and hidden from assistive tech. */}
          <span aria-hidden="true" style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
            {[
              { left: '3px', delay: '0s' },
              { left: '8px', delay: '0.16s' },
              { left: '13px', delay: '0.33s' },
            ].map((cap, i) => (
              <span
                key={i}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: cap.left,
                  width: '4px',
                  height: '7px',
                  borderRadius: '3px',
                  background: 'rgba(255,255,255,0.92)',
                  animation: 'pvCapsuleDrop 13s ease-in ' + cap.delay + ' infinite',
                }}
              />
            ))}
          </span>
        </span>
        <span
          style={{
            ...t.body,
            fontSize: '13.5px',
            fontWeight: 800,
            color: c.white,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}
        >
          {count > 99 ? '99+' : count}
        </span>
      </Link>
    </>
  );
};

export default CartFab;